/**
 * @architecture-header 1
 * @layer shared
 * @domain workspace-shell
 * @responsibility Own the navigation snapshot stack, panel chrome and transient notice state.
 * @state-owner module-scoped Zustand
 * @allowed-imports Zustand; shared view types; shared transient reset registry
 * @forbidden server entities; roles; permissions; tokens; localStorage; API client
 * @public-api workspace shell and modules
 * @side-effects in-memory UI updates only
 * @tests workspace-behaviour.test.ts
 */

import { create } from 'zustand';
import type { WorkspaceLanguage } from '../lib/i18n';
import type { WorkspaceSection } from '../lib/model';
import {
  registerWorkspaceTransientReset,
  type WorkspaceTransientResetOptions,
} from '../lib/transient-reset';

/** `mat` opens one of the class's materials without leaving the class context. */
export type OpenKind = 'stu' | 'minv' | 'ans' | 'rev' | 'prog' | 'iv' | 'mat';

export interface OpenReference {
  readonly k: OpenKind;
  readonly id?: string;
  readonly n?: string;
  readonly stu?: string | null;
  readonly q?: number | null;
}

export type PanelKey = 'list' | 'ref' | 'ivl' | 'ivr' | 'thr';

export const PANEL_DEFAULT_WIDTH: Readonly<Record<PanelKey, number>> = {
  list: 344,
  ref: 380,
  ivl: 210,
  ivr: 300,
  thr: 380,
};

export const PANEL_CSS_VARIABLE: Readonly<Record<PanelKey, string>> = {
  list: '--w-list',
  ref: '--w-ref',
  ivl: '--w-ivl',
  ivr: '--w-ivr',
  thr: '--w-thr',
};

export interface NavigationSnapshot {
  readonly section: WorkspaceSection;
  readonly selection: string | null;
  readonly classTab: string;
  readonly activityTab: string;
  readonly studentTab: string;
  readonly open: OpenReference | null;
}

export interface DisplayOptions {
  readonly view: 'list' | 'board';
  readonly grp: 'st' | 'ci';
  readonly ord: 'upd' | 'new' | 'name' | 'st' | 'tag';
  readonly props: readonly string[];
}

type FilterState = Readonly<Record<string, readonly string[]>>;

interface WorkspaceUiState {
  section: WorkspaceSection;
  selection: string | null;
  open: OpenReference | null;
  classTab: string;
  activityTab: string;
  studentTab: string;
  history: readonly NavigationSnapshot[];
  historyIndex: number;
  recent: readonly NavigationSnapshot[];
  /** The first shell `go` call seeds the URL-owned entry and is not a user visit. */
  initialSeedPending: boolean;
  mobileMainOpen: boolean;
  language: WorkspaceLanguage;
  listCollapsed: boolean;
  collapsedGroups: Readonly<Record<string, boolean>>;
  panelWidths: Readonly<Record<PanelKey, number>>;
  notice: string | null;
  filters: Readonly<Record<string, FilterState>>;
  display: Readonly<Record<string, DisplayOptions>>;
  /** A selection queued for a section that has not mounted yet (e.g. leaving the Settings
   *  route): the next `WorkspaceChrome` mount for that section consumes it once, instead of
   *  its own mount-time default. A module-level subscriber below clears this on ANY
   *  section/selection change that is not its own target -- whichever store action caused it
   *  (`go`, `pick`, `navigate`, `back`, `forward`, `navigateSnapshot`) -- so a mount this was
   *  never meant for can never resurrect it. */
  pendingSelection: { section: WorkspaceSection; selection: string } | null;

  go(section: WorkspaceSection, selection: string | null): void;
  pick(selection: string | null, isMobile?: boolean): void;
  navigate(
    section: WorkspaceSection,
    selection: string | null,
    options?: { open?: OpenReference | null; classTab?: string; activityTab?: string },
  ): void;
  openItem(reference: OpenReference): void;
  closeItem(): void;
  setTab(key: 'classTab' | 'activityTab' | 'studentTab', value: string): void;
  back(): void;
  forward(): void;
  navigateSnapshot(snapshot: NavigationSnapshot): void;
  closeMobileMain(): void;
  setLanguage(language: WorkspaceLanguage): void;
  toggleList(): void;
  toggleGroup(id: string): void;
  setPanelWidth(key: PanelKey, width: number): void;
  resetPanelWidth(key: PanelKey): void;
  notify(message: string): void;
  clearNotice(): void;
  setFilter(scope: string, key: string, values: readonly string[]): void;
  clearFilter(scope: string, key?: string): void;
  setDisplay(scope: string, options: DisplayOptions): void;
  setPendingSelection(section: WorkspaceSection, selection: string): void;
  consumePendingSelection(section: WorkspaceSection): string | null;
  reset(options?: WorkspaceTransientResetOptions): void;
}

function snapshotOf(state: WorkspaceUiState): NavigationSnapshot {
  return {
    section: state.section,
    selection: state.selection,
    classTab: state.classTab,
    activityTab: state.activityTab,
    studentTab: state.studentTab,
    open: state.open,
  };
}

function sameSnapshot(a: NavigationSnapshot | undefined, b: NavigationSnapshot): boolean {
  if (!a) return false;
  return (
    a.section === b.section &&
    a.selection === b.selection &&
    a.classTab === b.classTab &&
    a.activityTab === b.activityTab &&
    a.studentTab === b.studentTab &&
    JSON.stringify(a.open ?? null) === JSON.stringify(b.open ?? null)
  );
}

function snapshotTargetKey(snapshot: NavigationSnapshot): string {
  return JSON.stringify([snapshot.section, snapshot.selection, snapshot.open ?? null]);
}

function recordRecent(
  state: WorkspaceUiState,
  snapshot: NavigationSnapshot,
): readonly NavigationSnapshot[] {
  const key = snapshotTargetKey(snapshot);
  return [snapshot, ...state.recent.filter((entry) => snapshotTargetKey(entry) !== key)].slice(
    0,
    12,
  );
}

/** Drop the forward stack and append, but only when the snapshot actually changed. */
function pushHistory(
  state: WorkspaceUiState,
  next: Partial<WorkspaceUiState>,
): Partial<WorkspaceUiState> {
  const merged = { ...state, ...next } as WorkspaceUiState;
  const snapshot = snapshotOf(merged);
  if (sameSnapshot(state.history[state.historyIndex], snapshot)) return next;
  const history = [...state.history.slice(0, state.historyIndex + 1), snapshot];
  return {
    ...next,
    history,
    historyIndex: history.length - 1,
    recent: recordRecent(state, snapshot),
  };
}

function applySnapshot(snapshot: NavigationSnapshot): Partial<WorkspaceUiState> {
  return {
    section: snapshot.section,
    selection: snapshot.selection,
    classTab: snapshot.classTab,
    activityTab: snapshot.activityTab,
    studentTab: snapshot.studentTab,
    open: snapshot.open,
    mobileMainOpen: Boolean(snapshot.selection),
  };
}

const INITIAL_SNAPSHOT: NavigationSnapshot = {
  section: 'room',
  selection: 'c0',
  classTab: 'stu',
  activityTab: 'mat',
  studentTab: 'todo',
  open: null,
};

const INITIAL_STATE = {
  section: INITIAL_SNAPSHOT.section,
  selection: INITIAL_SNAPSHOT.selection,
  open: null as OpenReference | null,
  classTab: INITIAL_SNAPSHOT.classTab,
  activityTab: INITIAL_SNAPSHOT.activityTab,
  studentTab: INITIAL_SNAPSHOT.studentTab,
  history: [INITIAL_SNAPSHOT] as readonly NavigationSnapshot[],
  historyIndex: 0,
  recent: [] as readonly NavigationSnapshot[],
  initialSeedPending: true,
  mobileMainOpen: false,
  language: 'zh' as WorkspaceLanguage,
  listCollapsed: false,
  collapsedGroups: {} as Readonly<Record<string, boolean>>,
  panelWidths: PANEL_DEFAULT_WIDTH,
  notice: null as string | null,
  filters: {} as Readonly<Record<string, FilterState>>,
  display: {} as Readonly<Record<string, DisplayOptions>>,
  pendingSelection: null as { section: WorkspaceSection; selection: string } | null,
};

export const useWorkspaceUiStore = create<WorkspaceUiState>((set, get) => ({
  ...INITIAL_STATE,

  go: (section, selection) =>
    set((state) => {
      const next = pushHistory(state, {
        section,
        selection,
        open: null,
        mobileMainOpen: false,
        notice: null,
      });
      const snapshot = snapshotOf({ ...state, ...next } as WorkspaceUiState);
      const recent = state.initialSeedPending
        ? state.recent
        : sameSnapshot(state.history[state.historyIndex], snapshot)
          ? recordRecent(state, snapshot)
          : next.recent;
      return { ...next, initialSeedPending: false, recent };
    }),

  pick: (selection, isMobile = false) =>
    set((state) => pushHistory(state, { selection, mobileMainOpen: isMobile })),

  navigate: (section, selection, options = {}) =>
    set((state) =>
      pushHistory(state, {
        section,
        selection,
        open: options.open ?? null,
        classTab: options.classTab ?? state.classTab,
        activityTab: options.activityTab ?? state.activityTab,
        mobileMainOpen: false,
      }),
    ),

  openItem: (reference) => set((state) => pushHistory(state, { open: reference })),

  closeItem: () => set((state) => pushHistory(state, { open: null })),

  setTab: (key, value) =>
    set((state) => (state[key] === value ? {} : pushHistory(state, { [key]: value }))),

  back: () =>
    set((state) =>
      state.historyIndex > 0
        ? (() => {
            const snapshot = state.history[state.historyIndex - 1];
            return {
              ...applySnapshot(snapshot),
              historyIndex: state.historyIndex - 1,
            };
          })()
        : {},
    ),

  forward: () =>
    set((state) =>
      state.historyIndex < state.history.length - 1
        ? (() => {
            const snapshot = state.history[state.historyIndex + 1];
            return {
              ...applySnapshot(snapshot),
              historyIndex: state.historyIndex + 1,
            };
          })()
        : {},
    ),

  navigateSnapshot: (snapshot) =>
    set((state) =>
      pushHistory(state, {
        section: snapshot.section,
        selection: snapshot.selection,
        classTab: snapshot.classTab,
        activityTab: snapshot.activityTab,
        studentTab: snapshot.studentTab,
        open: snapshot.open,
        mobileMainOpen: Boolean(snapshot.selection),
      }),
    ),

  closeMobileMain: () => set({ mobileMainOpen: false }),

  setLanguage: (language) => set({ language }),

  toggleList: () => set((state) => ({ listCollapsed: !state.listCollapsed })),

  toggleGroup: (id) =>
    set((state) => ({
      collapsedGroups: { ...state.collapsedGroups, [id]: !state.collapsedGroups[id] },
    })),

  setPanelWidth: (key, width) =>
    set((state) => ({ panelWidths: { ...state.panelWidths, [key]: width } })),

  resetPanelWidth: (key) =>
    set((state) => ({ panelWidths: { ...state.panelWidths, [key]: PANEL_DEFAULT_WIDTH[key] } })),

  notify: (notice) => set({ notice }),
  clearNotice: () => set({ notice: null }),

  setFilter: (scope, key, values) =>
    set((state) => ({
      filters: { ...state.filters, [scope]: { ...(state.filters[scope] ?? {}), [key]: values } },
    })),

  clearFilter: (scope, key) =>
    set((state) => {
      if (!key) return { filters: { ...state.filters, [scope]: {} } };
      const next = { ...(state.filters[scope] ?? {}) };
      delete next[key];
      return { filters: { ...state.filters, [scope]: next } };
    }),

  setDisplay: (scope, options) =>
    set((state) => ({ display: { ...state.display, [scope]: options } })),

  // SH-F02 round-3 rework (Sol BLOCKING finding 3): a search result picked from a route that
  // must leave first (e.g. Settings) writes here; the destination section's WorkspaceChrome
  // mount effect consumes it once, yielding to it instead of its own mount-time default. `go`
  // still runs immediately too (round-2 rework) so state observed before the route change is
  // already correct; this queue is only for surviving the REAL remount that follows. The
  // module-level subscriber below is what keeps this from ever going stale (SH-F02 round-5
  // rework, Sol BLOCKING finding 1) -- this action only needs to write the value.
  setPendingSelection: (section, selection) => set({ pendingSelection: { section, selection } }),

  consumePendingSelection: (section) => {
    const pending = get().pendingSelection;
    if (!pending) return null;
    // SH-F02 round-6 rework (Sol BLOCKING finding 1): a mismatched consume must clear the entry
    // too, not just decline to return it -- otherwise it survives THIS attempt and can still be
    // returned by a later, correctly-targeted consume it was never meant for.
    set({ pendingSelection: null });
    return pending.section === section ? pending.selection : null;
  },

  // `language` is a backend-persisted user preference (SET-F03), not context-scoped navigation
  // state — a role/Organization switch (via resetWorkspaceTransientState with no options) must
  // not clobber it back to the hardcoded default. Doing so silently defeated WorkspaceShell's
  // one-shot hydration guard: a switch/reset that lands after hydration already applied the real
  // locale left no second chance to reapply it. Sign-out is the other caller of this reset, and
  // it opts in via `resetLanguage: true`: on a shared device, a locale carried over into the next
  // user's pre-hydration window would violate the clean-slate-between-users doctrine.
  reset: (options) =>
    set((state) => ({
      ...INITIAL_STATE,
      language: options?.resetLanguage ? INITIAL_STATE.language : state.language,
    })),
}));

registerWorkspaceTransientReset('workspace-ui', (options) =>
  useWorkspaceUiStore.getState().reset(options),
);

// SH-F02 round-5 rework (Sol BLOCKING finding 1): round 4 only invalidated a queued
// pendingSelection when `go` itself caused the intervening navigation -- `pick`, `navigate`,
// `back`, `forward` and `navigateSnapshot` could all change section/selection without going
// through `go`, leaving a stale entry that a LATER, unrelated mount of the pending section could
// still resurrect. Subscribing to the store itself (rather than instrumenting every navigation
// action individually) catches every one of them uniformly, including any added later.
//
// Gating on "did section/selection's VALUE change" is not enough: a navigation call that lands
// back on values equal to what was already there (e.g. `go('room', 'c0')` when already at
// room/c0) still counts as a navigation event Zustand's `set` always notifies for, and must still
// invalidate an unrelated pending entry. So this gates on the SET CALL ITSELF instead: any call
// that did not touch `pendingSelection` (i.e. was not `setPendingSelection` or
// `consumePendingSelection` queuing/clearing it) is some other action running while a selection
// was pending -- unless it happens to be the exact consuming navigation to the pending target,
// invalidate.
useWorkspaceUiStore.subscribe((state, previousState) => {
  const pending = previousState.pendingSelection;
  if (!pending) return;
  if (state.pendingSelection !== previousState.pendingSelection) return;
  if (state.section === pending.section && state.selection === pending.selection) return;
  useWorkspaceUiStore.setState({ pendingSelection: null });
});
